import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideLinks = [
  { name: 'CSSBuy Spreadsheet Guide 2026', path: '/article/best-cssbuy-spreadsheet-guide-2026' },
  { name: 'Kakobuy vs CSSBuy Comparison', path: '/article/kakobuy-vs-cssbuy-spreadsheet-comparison' },
  { name: 'Superbuy Premium Spreadsheet Guide', path: '/article/superbuy-spreadsheet-premium-guide' },
];

export default function Navbar() {
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeAll = () => {
    setCatOpen(false);
    setGuideOpen(false);
    setMobileOpen(false);
  };

  const navLinkClass = "text-sm text-foreground-600 hover:text-foreground-950 transition-colors font-body whitespace-nowrap cursor-pointer";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70' : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 md:px-8 max-w-[1440px] mx-auto">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" onClick={closeAll}>
            <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight">
              css<span className="text-accent-500">spreadsheet</span>
            </span>
            <span className="hidden sm:inline text-xs text-foreground-500 font-body">.ink</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={navLinkClass} onClick={closeAll}>Home</Link>
            <a href="#categories" className={navLinkClass}>Categories</a>
            <a href="#comparison" className={navLinkClass}>Comparison</a>
            <a href="#guides" className={navLinkClass}>Guides</a>
            <a href="#about" className={navLinkClass}>About</a>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
                className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors font-body whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute left-0 mt-2 w-72 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-sm">
                  {guideLinks.map((g) => (
                    <Link
                      key={g.path}
                      to={g.path}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      onClick={() => setGuideOpen(false)}
                    >
                      {g.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer font-label"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>

            <div ref={catRef} className="relative hidden md:block">
              <button
                onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer border border-background-200/70 font-label"
              >
                <i className="ri-grid-line text-sm"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setCatOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer border border-background-200/70"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-lg text-foreground-700"></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={closeAll}></div>
          <div ref={mobileRef} className="absolute right-0 top-0 bottom-0 w-[85%] max-w-[360px] bg-background-50 shadow-lg flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200/70">
              <Link to="/" className="font-heading text-lg font-bold text-foreground-950 tracking-tight" onClick={closeAll}>
                css<span className="text-accent-500">spreadsheet</span>
              </Link>
              <button
                onClick={closeAll}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-lg text-foreground-700"></i>
              </button>
            </div>

            <div className="flex-1 p-4 space-y-1">
              <Link to="/" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-home-4-line text-lg text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-950 font-label">Home</span>
              </Link>
              <a href="#categories" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-grid-line text-lg text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-950 font-label">Categories</span>
              </a>
              <a href="#comparison" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-bar-chart-box-line text-lg text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-950 font-label">Comparison</span>
              </a>
              <a href="#guides" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-book-open-line text-lg text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-950 font-label">Guides</span>
              </a>
              <a href="#about" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-information-line text-lg text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-950 font-label">About</span>
              </a>

              <div className="border-t border-background-200/70 my-3"></div>
              <p className="px-4 text-xs text-foreground-400 uppercase tracking-wider font-label mb-2">Shopping Guides</p>
              {guideLinks.map((g) => (
                <Link key={g.path} to={g.path} onClick={closeAll} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-article-line text-lg text-foreground-500"></i>
                  <span className="text-sm text-foreground-700 font-body">{g.name}</span>
                </Link>
              ))}

              <div className="border-t border-background-200/70 my-3"></div>
              <p className="px-4 text-xs text-foreground-400 uppercase tracking-wider font-label mb-2">Shop by Category</p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-up-line text-sm text-foreground-400"></i>
                  <span className="text-sm text-foreground-700 font-body">{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="p-4 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer font-label"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}